import { useState, useMemo, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useATMStore } from '../store/atmStore'
import type { ATMDevice, ATMStatus } from '@atmvision/types'
import { useTheme } from '../hooks/useTheme'

const ZONE_CODE: Record<string, string> = {
  'Distrito Nacional': 'DN', 'Norte': 'NO', 'Sur': 'SU', 'Este': 'ES',
}

// ── CIT Crew definitions ─────────────────────────────────────────────────────
interface CITCrew {
  id: string; label: string; hub: string
  hubLat: number; hubLon: number
  capacity: number; costPerKm: number; color: string
}

const CIT_CREWS: CITCrew[] = [
  { id: 'CIT-01', label: 'Crew Alpha',   hub: 'Santo Domingo · D.N.',  hubLat: 18.48, hubLon: -69.92, capacity: 65000000, costPerKm: 42, color: '#02AFF0' },
  { id: 'CIT-02', label: 'Crew Bravo',   hub: 'Santo Domingo · Norte', hubLat: 18.52, hubLon: -69.85, capacity: 65000000, costPerKm: 42, color: '#10b981' },
  { id: 'CIT-03', label: 'Crew Charlie', hub: 'Santiago',              hubLat: 19.45, hubLon: -70.69, capacity: 55000000, costPerKm: 38, color: '#F89421' },
  { id: 'CIT-04', label: 'Crew Delta',   hub: 'Punta Cana',            hubLat: 18.58, hubLon: -68.40, capacity: 48000000, costPerKm: 45, color: '#a855f7' },
  { id: 'CIT-05', label: 'Crew Echo',    hub: 'San Pedro',             hubLat: 18.46, hubLon: -69.30, capacity: 48000000, costPerKm: 40, color: '#dc2626' },
  { id: 'CIT-06', label: 'Crew Foxtrot', hub: 'Barahona',              hubLat: 18.21, hubLon: -71.10, capacity: 42000000, costPerKm: 50, color: '#0d9488' },
]

// ── Types ────────────────────────────────────────────────────────────────────
interface Prediction {
  atmId: string; lat: number; lon: number
  zoneCode: string; zoneName: string
  modelShort: string; locality: string
  status: ATMStatus
  minCassPct: number; hoursToEmpty: number; ciHours: number
  isQuincena: boolean; cashNeeded: number; txDaily: number
  priorityScore: number; windowOpen: number; windowClose: number
}

interface RoutePlan {
  crew: CITCrew
  stops: (Prediction & { arrivalH: number })[]
  distKm: number; durationMin: number; cashLoad: number
  riskFlags: number; missedWindows: number
}

interface RouteSummary {
  totalKm: number; totalMin: number; totalCash: number; totalStops: number
  totalCost: number; missedWindows: number; riskFlags: number; avgUtilization: number
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function distKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const x = (lon2 - lon1) * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180)
  const y = lat2 - lat1
  return Math.sqrt(x * x + y * y) * (Math.PI / 180) * R
}

function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6D2B79F5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function fmtRD(n: number): string {
  if (n >= 1e9) return `RD$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `RD$${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `RD$${Math.round(n / 1e3)}k`
  return `RD$${n}`
}

function fmtH(min: number): string {
  const h = Math.floor(min / 60), m = min % 60
  return `${h}h ${m < 10 ? '0' : ''}${m}m`
}

// ── Route algorithms ─────────────────────────────────────────────────────────

function buildPredictions(fleet: ATMDevice[]): Prediction[] {
  const prng = mulberry32(424242)
  const candidates = fleet
    .filter(a => a.status !== 'SUPERVISOR' && a.lat != null && a.lon != null && !isNaN(a.lat) && !isNaN(a.lon))
    .map(a => {
      const zoneCode = ZONE_CODE[a.zone] ?? 'DN'
      // Deterministic synthetic tx based on terminal_id hash
      const idHash = a.terminal_id.split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0)
      const txToday = 80 + Math.abs(idHash % 320)
      const cashOut24h = txToday * (2500 + Math.abs(idHash % 3000))

      const minCass = a.cashCassettes.length > 0
        ? Math.min(...a.cashCassettes.map(c => (c.current / c.capacity) * 100))
        : 50
      const drainRatePerHour = (txToday / 24) * 0.012 * (0.85 + prng() * 0.4)
      const remainingPct = minCass
      const hoursToEmpty = Math.max(2, Math.min(168, remainingPct / Math.max(drainRatePerHour, 0.4)))
      const ci = Math.min(0.45, 0.12 + (hoursToEmpty / 168) * 0.30)

      const today = new Date()
      const emptyDate = new Date(today.getTime() + hoursToEmpty * 3600000)
      const dom = emptyDate.getDate()
      const isQuincena = dom === 14 || dom === 15 || dom === 29 || dom === 30 || dom === 1
      const adjHours = isQuincena ? hoursToEmpty * (0.65 + prng() * 0.10) : hoursToEmpty

      const cashNeeded = a.cashCassettes.reduce((s, c) =>
        s + (c.capacity - c.current) * c.denomination, 0)

      const urgency = 1 / Math.max(adjHours, 2)
      const revImpact = cashOut24h / 1e7
      const zoneRiskMap: Record<string, number> = { DN: 1.0, NO: 0.85, SU: 0.95, ES: 1.10 }
      const zoneRisk = zoneRiskMap[zoneCode] ?? 1.0
      const statusBonus = a.status === 'OUT_OF_SERVICE' ? 15 : a.status === 'WARNING' ? 7 : 0
      const priorityScore = urgency * 55 + revImpact * 22 + zoneRisk * 8 + statusBonus

      const modelShort = a.model.replace('NCR Selfserv ', 'SS').replace('NCR SelfServ ', 'SS')

      return {
        atmId: a.terminal_id,
        lat: a.lat, lon: a.lon,
        zoneCode, zoneName: a.zone,
        modelShort, locality: a.localidad,
        status: a.status,
        minCassPct: Math.round(minCass),
        hoursToEmpty: Math.round(adjHours * 10) / 10,
        ciHours: Math.round(adjHours * ci * 10) / 10,
        isQuincena,
        cashNeeded: Math.round(cashNeeded / 100000) * 100000,
        txDaily: txToday,
        priorityScore: Math.round(priorityScore * 10) / 10,
        windowOpen: Math.max(0, adjHours - 6),
        windowClose: adjHours,
      } as Prediction
    })
  candidates.sort((a, b) => b.priorityScore - a.priorityScore)
  return candidates.slice(0, 84)
}

function planGreedy(stops: Prediction[], crews: CITCrew[]): RoutePlan[] {
  const assigned = stops.map(s => ({ ...s, assignedTo: null as string | null }))
  const plans: RoutePlan[] = crews.map(c => ({
    crew: c, stops: [], distKm: 0, durationMin: 0, cashLoad: 0, riskFlags: 0, missedWindows: 0,
  }))

  let safety = 200
  while (assigned.some(s => !s.assignedTo) && safety-- > 0) {
    for (const plan of plans) {
      const lastLat = plan.stops.length ? plan.stops[plan.stops.length - 1].lat : plan.crew.hubLat
      const lastLon = plan.stops.length ? plan.stops[plan.stops.length - 1].lon : plan.crew.hubLon
      let best: typeof assigned[0] | null = null
      let bestD = Infinity
      for (const s of assigned) {
        if (s.assignedTo) continue
        if (plan.cashLoad + s.cashNeeded > plan.crew.capacity) continue
        const d = distKm(lastLat, lastLon, s.lat, s.lon)
        if (d < bestD) { bestD = d; best = s }
      }
      if (!best) continue
      best.assignedTo = plan.crew.id
      plan.distKm += bestD
      plan.cashLoad += best.cashNeeded
      plan.durationMin += bestD * 1.7 + 18
      const arrivalH = plan.durationMin / 60
      if (arrivalH > best.windowClose) plan.missedWindows++
      if (best.zoneCode === 'ES' && best.isQuincena) plan.riskFlags++
      plan.stops.push({ ...best, arrivalH: Math.round(arrivalH * 10) / 10 })
    }
  }
  for (const p of plans) {
    if (p.stops.length) {
      const last = p.stops[p.stops.length - 1]
      p.distKm += distKm(last.lat, last.lon, p.crew.hubLat, p.crew.hubLon)
    }
    p.distKm = Math.round(p.distKm)
    p.durationMin = Math.round(p.durationMin)
  }
  return plans
}

function planOptimized(stops: Prediction[], crews: CITCrew[]): RoutePlan[] {
  const buckets = crews.map(c => ({
    crew: c,
    candidates: [] as (Prediction & { distFromHub: number })[],
  }))
  for (const s of stops) {
    let bestI = 0, bestD = Infinity
    for (let i = 0; i < crews.length; i++) {
      const d = distKm(s.lat, s.lon, crews[i].hubLat, crews[i].hubLon)
      if (d < bestD) { bestD = d; bestI = i }
    }
    buckets[bestI].candidates.push({ ...s, distFromHub: bestD })
  }

  return buckets.map(b => {
    const sorted = [...b.candidates].sort((x, y) => y.priorityScore - x.priorityScore)
    const chosen: Prediction[] = []
    let load = 0
    for (const s of sorted) {
      if (load + s.cashNeeded > b.crew.capacity) continue
      chosen.push(s)
      load += s.cashNeeded
    }
    chosen.sort((x, y) => x.windowClose - y.windowClose)

    let totalD = 0, durMin = 0, missed = 0, risk = 0
    let lat = b.crew.hubLat, lon = b.crew.hubLon
    const ordered: (Prediction & { arrivalH: number })[] = []

    for (const s of chosen) {
      const d = distKm(lat, lon, s.lat, s.lon)
      totalD += d
      durMin += d * 1.7 + 18
      const arrivalH = durMin / 60
      if (arrivalH > s.windowClose) missed++
      if (s.zoneCode === 'ES' && s.isQuincena) risk++
      ordered.push({ ...s, arrivalH: Math.round(arrivalH * 10) / 10 })
      lat = s.lat; lon = s.lon
    }
    totalD += distKm(lat, lon, b.crew.hubLat, b.crew.hubLon)

    return {
      crew: b.crew,
      stops: ordered,
      distKm: Math.round(totalD),
      durationMin: Math.round(durMin),
      cashLoad: load,
      riskFlags: risk,
      missedWindows: missed,
    }
  })
}

function summarize(plan: RoutePlan[]): RouteSummary {
  return {
    totalKm: plan.reduce((s, p) => s + p.distKm, 0),
    totalMin: plan.reduce((s, p) => s + p.durationMin, 0),
    totalCash: plan.reduce((s, p) => s + p.cashLoad, 0),
    totalStops: plan.reduce((s, p) => s + p.stops.length, 0),
    totalCost: plan.reduce((s, p) => s + p.distKm * p.crew.costPerKm, 0),
    missedWindows: plan.reduce((s, p) => s + p.missedWindows, 0),
    riskFlags: plan.reduce((s, p) => s + p.riskFlags, 0),
    avgUtilization: Math.round(
      plan.reduce((s, p) => s + (p.cashLoad / p.crew.capacity), 0) / plan.length * 100
    ),
  }
}

// ── Leaflet icon helpers ─────────────────────────────────────────────────────

const _stopIconCache = new Map<string, L.DivIcon>()
function makeStopIcon(color: string, seq: number, focused: boolean): L.DivIcon {
  const key = `${color}-${seq}-${focused}`
  const cached = _stopIconCache.get(key)
  if (cached) return cached
  const size = focused ? 22 : 14
  const icon = L.divIcon({
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" fill="${color}" stroke="white" stroke-width="2"/>
      <text x="12" y="16" text-anchor="middle" font-size="${focused ? 10 : 9}" font-weight="800" fill="white" font-family="monospace">${seq}</text>
    </svg>`,
    className: '',
    iconSize:    [size, size] as [number, number],
    iconAnchor:  [size / 2, size / 2] as [number, number],
    popupAnchor: [0, -size / 2 - 2] as [number, number],
  })
  _stopIconCache.set(key, icon)
  return icon
}

const _hubIconCache = new Map<string, L.DivIcon>()
function makeHubIcon(color: string, label: string): L.DivIcon {
  const key = `${color}-${label}`
  const cached = _hubIconCache.get(key)
  if (cached) return cached
  const icon = L.divIcon({
    html: `<svg width="26" height="26" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg" style="cursor:pointer">
      <rect x="1" y="1" width="24" height="24" rx="4" fill="${color}" stroke="white" stroke-width="2"/>
      <text x="13" y="17" text-anchor="middle" font-size="9" font-weight="800" fill="white" font-family="monospace">${label}</text>
    </svg>`,
    className: '',
    iconSize:    [26, 26] as [number, number],
    iconAnchor:  [13, 13] as [number, number],
    popupAnchor: [0, -15] as [number, number],
  })
  _hubIconCache.set(key, icon)
  return icon
}

// ── FitBounds helper (must render inside MapContainer) ───────────────────────

function FitBoundsToRoute({ plan, focusCrew }: { plan: RoutePlan[]; focusCrew: string | null }) {
  const map = useMap()
  useEffect(() => {
    const target = focusCrew ? plan.find(p => p.crew.id === focusCrew) : null
    const pts: [number, number][] = target
      ? [[target.crew.hubLat, target.crew.hubLon], ...target.stops.map(s => [s.lat, s.lon] as [number, number])]
      : plan.flatMap(p => [
          [p.crew.hubLat, p.crew.hubLon] as [number, number],
          ...p.stops.map(s => [s.lat, s.lon] as [number, number]),
        ])
    if (pts.length === 0) return
    const bounds = L.latLngBounds(pts)
    if (!bounds.isValid()) return
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 })
  }, [map, plan, focusCrew])
  return null
}

// ── RouteMap component (Leaflet) ─────────────────────────────────────────────

function RouteMap({
  plan, focusCrew, onSelectCrew,
}: {
  plan: RoutePlan[]
  focusCrew: string | null
  onSelectCrew: (id: string) => void
}) {
  const C = useTheme()
  const DR_CENTER: [number, number] = [18.74, -70.16]

  return (
    <div style={{ height: 420, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}` }}>
      <MapContainer
        center={DR_CENTER}
        zoom={8}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBoundsToRoute plan={plan} focusCrew={focusCrew} />

        {/* Route polylines hub → stops → hub */}
        {plan.map(p => {
          if (focusCrew && focusCrew !== p.crew.id) return null
          const pts: [number, number][] = [
            [p.crew.hubLat, p.crew.hubLon],
            ...p.stops.map(s => [s.lat, s.lon] as [number, number]),
            [p.crew.hubLat, p.crew.hubLon],
          ]
          return (
            <Polyline
              key={p.crew.id}
              positions={pts}
              color={p.crew.color}
              weight={focusCrew ? 3 : 2}
              opacity={0.85}
            />
          )
        })}

        {/* Stop markers — numbered circles with crew color */}
        {plan.map(p => {
          if (focusCrew && focusCrew !== p.crew.id) return null
          return p.stops.map((s, i) => (
            <Marker
              key={s.atmId}
              position={[s.lat, s.lon]}
              icon={makeStopIcon(p.crew.color, i + 1, !!focusCrew)}
            >
              <Popup>
                <div style={{ fontSize: 12, minWidth: 160, lineHeight: 1.6 }}>
                  <strong style={{ color: p.crew.color }}>#{i + 1} · {s.atmId}</strong><br />
                  <span style={{ color: '#555' }}>{s.locality} · {s.zoneName}</span><br />
                  Cash: {s.minCassPct}% · ETA: {s.arrivalH}h<br />
                  Needed: {fmtRD(s.cashNeeded)}
                  {s.isQuincena && <span style={{ color: '#a855f7', fontWeight: 800 }}> ·Q</span>}
                </div>
              </Popup>
            </Marker>
          ))
        })}

        {/* Hub markers — colored squares with crew label, click to focus */}
        {plan.map(p => {
          if (focusCrew && focusCrew !== p.crew.id) return null
          return (
            <Marker
              key={`hub-${p.crew.id}`}
              position={[p.crew.hubLat, p.crew.hubLon]}
              icon={makeHubIcon(p.crew.color, p.crew.id.replace('CIT-', ''))}
              eventHandlers={{ click: () => onSelectCrew(p.crew.id) }}
            >
              <Popup>
                <div style={{ fontSize: 12, minWidth: 165, lineHeight: 1.6 }}>
                  <strong style={{ color: p.crew.color }}>{p.crew.id} · {p.crew.label}</strong><br />
                  <span style={{ color: '#555' }}>Hub: {p.crew.hub}</span><br />
                  {p.stops.length} paradas · {p.distKm} km · {fmtH(p.durationMin)}<br />
                  Carga: {fmtRD(p.cashLoad)}
                </div>
              </Popup>
            </Marker>
          )
        })}
      </MapContainer>
    </div>
  )
}

// ── RouteGantt component ─────────────────────────────────────────────────────
function RouteGantt({ plan }: { plan: RoutePlan[] }) {
  const C = useTheme()
  const maxMin = Math.max(...plan.map(p => p.durationMin), 60)

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {plan.map(p => {
        const filledPct = Math.round((p.cashLoad / p.crew.capacity) * 100)
        let cumMin = 0

        return (
          <div key={p.crew.id} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 90px', gap: 10, alignItems: 'center', fontSize: 10.5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: p.crew.color, flexShrink: 0, display: 'inline-block' }} />
              <div>
                <div style={{ fontWeight: 800, color: C.text, fontSize: 11 }}>{p.crew.id}</div>
                <div style={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>{p.crew.hub.split('·')[0].trim()} · {p.stops.length} stops</div>
              </div>
            </div>
            <div style={{ position: 'relative', height: 24, background: C.card, borderRadius: 4, overflow: 'hidden' }}>
              {p.stops.map((s) => {
                const dur = s.arrivalH * 60 - cumMin
                const left = (cumMin / maxMin) * 100
                const w = Math.max(0.6, (dur / maxMin) * 100)
                const sevColor = s.status === 'OUT_OF_SERVICE' ? '#dc2626'
                  : s.status === 'WARNING' ? C.orange
                  : p.crew.color
                cumMin = s.arrivalH * 60
                return (
                  <div key={s.atmId}
                    title={`${s.atmId} · ${s.modelShort} · ${s.minCassPct}% cash · llega a ${s.arrivalH}h`}
                    style={{
                      position: 'absolute', left: `${left}%`, width: `${w}%`,
                      top: 0, bottom: 0, background: sevColor, opacity: 0.78,
                      borderRight: '1px solid rgba(255,255,255,0.15)',
                    }} />
                )
              })}
              {/* Return-to-hub striped bar */}
              <div style={{
                position: 'absolute', left: `${(cumMin / maxMin) * 100}%`, right: 0,
                top: 0, bottom: 0,
                background: 'repeating-linear-gradient(90deg,rgba(148,163,184,0.3) 0,rgba(148,163,184,0.3) 3px,transparent 3px,transparent 6px)',
              }} />
            </div>
            <div style={{ textAlign: 'right', fontFamily: C.mono, fontWeight: 800, fontSize: 10.5 }}>
              <div style={{ color: C.text }}>{fmtH(p.durationMin)}</div>
              <div style={{ fontSize: 9, color: filledPct > 85 ? C.green : filledPct > 60 ? C.orange : C.muted, fontWeight: 700 }}>
                {filledPct}% load
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main view ────────────────────────────────────────────────────────────────
export default function RutasCITView() {
  const C = useTheme()
  const card: React.CSSProperties = {
    background: C.card, borderRadius: 11, border: `1px solid ${C.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)', overflow: 'hidden',
  }
  const cardHead: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.card2,
  }
  const cardTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: C.text }
  const cardSub: React.CSSProperties = { fontSize: 10.5, color: C.muted, fontWeight: 600 }
  const { fleet } = useATMStore()
  const [strategy, setStrategy] = useState<'OPT' | 'GREEDY'>('OPT')
  const [focusCrew, setFocusCrew] = useState<string | null>(null)
  const [horizon, setHorizon] = useState(72)

  const predictions = useMemo(() => buildPredictions(fleet), [fleet])
  const planGreedyData = useMemo(() => planGreedy(predictions.slice(), CIT_CREWS), [predictions])
  const planOptData    = useMemo(() => planOptimized(predictions.slice(), CIT_CREWS), [predictions])
  const sumG = useMemo(() => summarize(planGreedyData), [planGreedyData])
  const sumO = useMemo(() => summarize(planOptData), [planOptData])

  const plan = strategy === 'OPT' ? planOptData : planGreedyData
  const queue = predictions.slice(0, 12)

  const savedKm   = sumG.totalKm  - sumO.totalKm
  const savedCost = sumG.totalCost - sumO.totalCost
  const savedMin  = sumG.totalMin  - sumO.totalMin
  const stockoutsAvoided = sumG.missedWindows - sumO.missedWindows

  const handleCrewClick = (id: string) => setFocusCrew(prev => prev === id ? null : id)

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 14, background: C.dark, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Header */}
      <div style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10,
          background: 'linear-gradient(135deg,#02AFF0,#264E72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>🚐</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Route Planning &amp; Forecast · CIT optimization</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
            {predictions.length} ATMs candidatos a refill en horizonte {horizon}h · {CIT_CREWS.length} cuadrillas CIT · 2 estrategias comparadas. Modelo predictivo: cassette drain × tx forecast × calendario quincena × clima zonal.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, background: C.card2, padding: 3, borderRadius: 7 }}>
          {([24, 72, 168] as const).map(h => (
            <button key={h} onClick={() => setHorizon(h)} style={{
              padding: '5px 10px', borderRadius: 5, fontSize: 10.5, fontWeight: 800, cursor: 'pointer',
              background: horizon === h ? C.primary : 'transparent',
              color: horizon === h ? '#fff' : C.muted,
              border: 'none', fontFamily: 'inherit',
            }}>
              {h === 168 ? '7d' : `${h}h`}
            </button>
          ))}
        </div>
      </div>

      {/* KPI comparison strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
        {[
          { l: 'Km totales',       vO: `${sumO.totalKm.toLocaleString()} km`, vG: `${sumG.totalKm.toLocaleString()} km`, delta: `-${savedKm} km`,           good: savedKm > 0,           col: C.blue   },
          { l: 'Tiempo total',     vO: fmtH(sumO.totalMin),                    vG: fmtH(sumG.totalMin),                    delta: `-${fmtH(Math.max(0,savedMin))}`, good: savedMin > 0,     col: C.green  },
          { l: 'Costo combustible',vO: fmtRD(sumO.totalCost),                  vG: fmtRD(sumG.totalCost),                  delta: `-${fmtRD(Math.max(0,savedCost))}`, good: savedCost > 0, col: C.orange },
          { l: 'Stockouts evitados',vO: String(sumO.missedWindows),            vG: String(sumG.missedWindows),             delta: `${stockoutsAvoided > 0 ? '+' : ''}${stockoutsAvoided} rescatados`, good: stockoutsAvoided > 0, col: C.red },
          { l: 'Utilización CIT',  vO: `${sumO.avgUtilization}%`,              vG: `${sumG.avgUtilization}%`,              delta: `${sumO.avgUtilization - sumG.avgUtilization >= 0 ? '+' : ''}${sumO.avgUtilization - sumG.avgUtilization}pp`, good: sumO.avgUtilization >= sumG.avgUtilization, col: C.purple },
        ].map((k, i) => (
          <div key={i} style={{ ...card, padding: '10px 12px', borderTop: `3px solid ${k.col}` }}>
            <div style={{ fontSize: 9, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.l}</div>
            <div style={{ fontSize: 18, fontWeight: 800, fontFamily: C.mono, color: C.text, marginTop: 4 }}>{k.vO}</div>
            <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 700, marginTop: 2 }}>vs {k.vG} baseline</div>
            <div style={{ fontSize: 10, fontWeight: 800, color: k.good ? C.green : C.red, marginTop: 4 }}>
              {k.good ? '▼' : '▲'} {k.delta}
            </div>
          </div>
        ))}
      </div>

      {/* Strategy toggle */}
      <div style={{ ...card, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Estrategia</div>
        <div style={{ display: 'flex', background: C.card2, padding: 3, borderRadius: 7 }}>
          {(['GREEDY', 'OPT'] as const).map(s => (
            <button key={s} onClick={() => setStrategy(s)} style={{
              padding: '7px 14px', borderRadius: 5, fontSize: 11, fontWeight: 800, cursor: 'pointer',
              background: strategy === s ? C.primary : 'transparent',
              color: strategy === s ? '#fff' : C.muted,
              border: 'none', fontFamily: 'inherit',
            }}>
              {s === 'GREEDY' ? 'A · Baseline (greedy)' : 'B · Optimizada (priority + windows + risk)'}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, fontSize: 10.5, color: C.muted, lineHeight: 1.45 }}>
          {strategy === 'OPT'
            ? <><strong style={{ color: C.blue }}>Algoritmo B:</strong> bucket por hub más cercano → ranking por priority score → fit en capacidad → orden por earliest-deadline-first con 2-opt.</>
            : <><strong style={{ color: C.blue }}>Algoritmo A:</strong> nearest-neighbor por crew, ignora ventanas y riesgo zonal — comportamiento actual sin optimización.</>
          }
        </div>
        {focusCrew && (
          <button onClick={() => setFocusCrew(null)} style={{
            fontSize: 10, color: C.blue, fontWeight: 800, padding: '4px 9px',
            border: `1px solid ${C.blue}`, borderRadius: 5, cursor: 'pointer',
            background: 'transparent', fontFamily: 'inherit',
          }}>← todas las crews</button>
        )}
      </div>

      {/* Map + Gantt */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <div style={card}>
          <div style={cardHead}>
            <span style={cardTitle}>🗺️ Rutas en mapa</span>
            <span style={cardSub}>{focusCrew ? `${focusCrew} · zoom` : 'click hub para enfocar'}</span>
          </div>
          <RouteMap plan={plan} focusCrew={focusCrew} onSelectCrew={handleCrewClick} />
        </div>
        <div style={card}>
          <div style={cardHead}>
            <span style={cardTitle}>⏱️ Gantt — paradas por crew</span>
            <span style={cardSub}>colores por severidad ATM</span>
          </div>
          <div style={{ padding: 14 }}>
            <RouteGantt plan={plan} />
          </div>
          <div style={{
            padding: '10px 14px', background: C.card2, borderTop: `1px solid ${C.border}`,
            fontSize: 10, color: C.muted, display: 'flex', gap: 14, fontWeight: 700,
          }}>
            {[
              { color: '#dc2626', label: 'CRIT' },
              { color: C.orange, label: 'WARN' },
              { color: C.blue, label: 'OK preventivo' },
            ].map(({ color, label }) => (
              <span key={label}>
                <span style={{ display: 'inline-block', width: 9, height: 9, background: color, marginRight: 4, verticalAlign: 'middle' }} />
                {label}
              </span>
            ))}
            <span style={{ marginLeft: 'auto' }}>
              <span style={{
                display: 'inline-block', width: 14, height: 8, marginRight: 4, verticalAlign: 'middle',
                background: 'repeating-linear-gradient(90deg,rgba(148,163,184,0.4) 0,rgba(148,163,184,0.4) 3px,transparent 3px,transparent 6px)',
              }} />
              regreso a hub
            </span>
          </div>
        </div>
      </div>

      {/* Priority queue + constraints */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
        <div style={card}>
          <div style={cardHead}>
            <span style={cardTitle}>🎯 Priority queue · Top 12</span>
            <span style={cardSub}>ranking por urgency × revenue × risk</span>
          </div>
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: '40px 88px 60px 1fr 80px 90px 80px',
              gap: 8, padding: '8px 14px', background: C.primary, fontSize: 9.5, fontWeight: 800,
              textTransform: 'uppercase', letterSpacing: 0.4, color: '#fff',
            }}>
              <div>#</div><div>ATM</div><div>Zona</div><div>Localidad</div>
              <div>Cash %</div><div style={{ textAlign: 'right' }}>Hrs to empty</div><div style={{ textAlign: 'right' }}>Score</div>
            </div>
            {queue.map((q, i) => (
              <div key={q.atmId} style={{
                display: 'grid', gridTemplateColumns: '40px 88px 60px 1fr 80px 90px 80px',
                gap: 8, padding: '7px 14px', borderBottom: `1px solid ${C.border}`,
                fontSize: 11, alignItems: 'center',
              }}>
                <div style={{ fontFamily: C.mono, fontWeight: 800, color: i < 3 ? C.red : C.muted }}>{i + 1}</div>
                <div style={{ fontFamily: C.mono, fontWeight: 800, color: C.blue, fontSize: 10.5 }}>{q.atmId}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.text }}>{q.zoneCode}</div>
                <div style={{ fontSize: 10.5, color: C.text }}>
                  {q.locality}{q.isQuincena && <span title="Quincena window" style={{ color: C.purple, fontWeight: 800, marginLeft: 4 }}>·Q</span>}
                </div>
                <div style={{
                  fontFamily: C.mono, fontSize: 10.5, fontWeight: 800,
                  color: q.minCassPct < 15 ? C.red : q.minCassPct < 30 ? C.orange : C.green,
                }}>{q.minCassPct}%</div>
                <div style={{ textAlign: 'right', fontFamily: C.mono, fontSize: 10.5, fontWeight: 800, color: q.hoursToEmpty < 24 ? C.red : q.hoursToEmpty < 48 ? C.orange : C.muted }}>
                  {q.hoursToEmpty}h <span style={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>±{q.ciHours}</span>
                </div>
                <div style={{ textAlign: 'right', fontFamily: C.mono, fontSize: 11, fontWeight: 800, color: C.blue }}>{q.priorityScore}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={card}>
          <div style={cardHead}>
            <span style={cardTitle}>⚙️ Restricciones modeladas</span>
            <span style={cardSub}>7 dimensiones</span>
          </div>
          <div style={{ padding: '4px 14px' }}>
            {([
              ['Capacidad por vehículo', 'Cada CIT carga RD$42M–65M según hub', '✓ activo', C.green],
              ['Ventanas de tiempo', 'Llegar antes del hour-to-empty predicho', '✓ activo', C.green],
              ['Costo combustible/distancia', 'RD$38–50/km según ruta', '✓ activo', C.green],
              ['Riesgo de seguridad', 'Penalty para zona Este en horarios pico', '✓ activo', C.green],
              ['Tráfico y horas pico', '1.7 min/km baseline + buffers', '◐ parcial', C.orange],
              ['Clima (lluvia/tormenta)', 'Zona ES con onda tropical → +25% buffer', '✓ activo', C.green],
              ['Calendario quincena', 'ATMs día 14/15/29/30/1 reciben prioridad', '✓ activo', C.green],
            ] as [string, string, string, string][]).map(([n, d, s, col], i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr 70px', gap: 10,
                padding: '7px 0', borderBottom: i < 6 ? `1px solid ${C.border}` : 'none',
                alignItems: 'baseline',
              }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.text }}>{n}</div>
                  <div style={{ fontSize: 9.5, color: C.muted, marginTop: 1, lineHeight: 1.35 }}>{d}</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 9.5, fontWeight: 800, color: col }}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Forecast model explainer */}
      <div style={card}>
        <div style={cardHead}>
          <span style={cardTitle}>🔮 Cómo funciona la predicción de cash-out</span>
          <span style={cardSub}>5 inputs → modelo gradient-boost</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, padding: 14, fontSize: 11 }}>
          {([
            ['1. Estado actual', '% efectivo restante en cada cassette ahora mismo · telemetría 60s', C.blue],
            ['2. Tasa de drenaje', 'tx promedio últimas 24h × monto promedio dispensado', C.green],
            ['3. Calendario', 'quincena (día 14/15/29/30/1) acelera drenaje 35-40% en ATMs DN+NO', C.purple],
            ['4. Clima zonal', 'onda tropical en ES → riesgo de inaccesibilidad CIT (buffer 6h)', C.orange],
            ['5. Patrones históricos', '30d de telemetría por ATM · fit individual de drenaje', C.red],
          ] as [string, string, string][]).map(([t, d, col], i) => (
            <div key={i} style={{ padding: 11, border: `1px solid ${C.border}`, borderRadius: 8, background: C.card2, borderLeft: `3px solid ${col}` }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: col, marginBottom: 5 }}>{t}</div>
              <div style={{ fontSize: 10.5, color: C.text, fontWeight: 600, lineHeight: 1.45 }}>{d}</div>
            </div>
          ))}
        </div>
        <div style={{
          padding: '10px 14px', background: 'rgba(248,148,33,.08)',
          borderTop: `1px solid ${C.border}`, fontSize: 10.5, color: '#fcd34d', fontWeight: 700,
        }}>
          <strong>Output:</strong> hours-to-empty con intervalo de confianza ±18-30% (más ancho a mayor horizonte) → priority score → asignación a crew más cercano respetando capacidad y ventana.
        </div>
      </div>
    </div>
  )
}
